import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L, { LatLngExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Importa las imágenes de los iconos
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Solución para iconos predeterminados de Leaflet en Webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface LeafletMapProps {
  position: LatLngExpression;
  editable?: boolean;
  onPositionChange?: (position: LatLngExpression) => void;
}

const MapControlHandler: React.FC<{ editable: boolean }> = React.memo(({ editable }) => {
  const map = useMap();

  useEffect(() => {
    if (editable) {
      map.scrollWheelZoom.enable();
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
    } else {
      map.scrollWheelZoom.disable();
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
    }
  }, [editable, map]);

  return null;
});

MapControlHandler.displayName = 'MapControlHandler';

const MarkerComponent = React.memo(
  ({ position, editable, onPositionChange }: { position: LatLngExpression; editable: boolean; onPositionChange?: (position: LatLngExpression) => void }) => {
    const [markerPosition, setMarkerPosition] = useState<LatLngExpression>(position);
    const map = useMap();

    useEffect(() => {
      if (markerPosition !== position) {
        setMarkerPosition(position);
        map.flyTo(position, map.getZoom(), { animate: true, duration: 1.0 });
      }
    }, [position, map, markerPosition]);

    useMapEvents({
      click(e) {
        if (editable) {
          setMarkerPosition(e.latlng);
          onPositionChange && onPositionChange(e.latlng);

          map.flyTo(e.latlng, map.getZoom(), { animate: true, duration: 1.0 });
        }
      },
    });

    const eventHandlers = useMemo(
      () => ({
        dragend(event: L.DragEndEvent) {
          const latlng = event.target.getLatLng();
          setMarkerPosition(latlng);
          onPositionChange && onPositionChange(latlng);

          map.flyTo(latlng, map.getZoom(), { animate: true, duration: 1.0 });
        },
      }),
      [map, onPositionChange]
    );

    return <Marker position={markerPosition} draggable={editable} eventHandlers={eventHandlers} />;
  }
);

MarkerComponent.displayName = 'MarkerComponent';

const LeafletMap: React.FC<LeafletMapProps> = React.memo(({ position, editable = false, onPositionChange }) => {
  const initialPosition = useMemo(() => position, []); // Solo calcula la posición inicial una vez.

  return (
    <MapContainer center={initialPosition} zoom={14} zoomControl={true} style={{ height: '400px', width: '100%' }}>
      <TileLayer
        attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      <MapControlHandler editable={editable} />
      <MarkerComponent position={position} editable={editable} onPositionChange={onPositionChange} />
    </MapContainer>
  );
});

LeafletMap.displayName = 'LeafletMap';

export default LeafletMap;
